import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api } from '../lib/api';
import { getRawToken } from '../lib/tokenStorage';
import { logger } from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Filter, Paperclip, Send, Loader2, Trash2, RefreshCw, X, Download, MessageSquare, User, Calendar as CalIcon } from 'lucide-react';
import { useAuth } from '../lib/auth-context';

// ★ Fase 32 — etichette localizzate a runtime (getter → lingua attiva)
const STATUS_META = {
  get open() { return { label: `🆕 ${i18n.t('ticketsPage.stOpen', 'Aperto')}`, cls: 'bg-blue-100 text-blue-800 border-blue-200' }; },
  get in_progress() { return { label: `⏳ ${i18n.t('ticketsPage.stInProgress', 'In lavorazione')}`, cls: 'bg-amber-100 text-amber-800 border-amber-200' }; },
  get resolved() { return { label: `✅ ${i18n.t('ticketsPage.stResolved', 'Risolto')}`, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' }; },
  get closed() { return { label: `🔒 ${i18n.t('ticketsPage.stClosed', 'Chiuso')}`, cls: 'bg-slate-100 text-slate-700 border-slate-200' }; },
};
const PRIORITY_META = {
  get low() { return { label: i18n.t('ticketsPage.prLow', 'Bassa'), cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }; },
  get medium() { return { label: i18n.t('ticketsPage.prMedium', 'Media'), cls: 'bg-amber-50 text-amber-700 border-amber-200' }; },
  get high() { return { label: i18n.t('ticketsPage.prHigh', 'Alta'), cls: 'bg-orange-50 text-orange-700 border-orange-200' }; },
  get critical() { return { label: i18n.t('ticketsPage.prCritical', 'Critica'), cls: 'bg-rose-50 text-rose-700 border-rose-200' }; },
};
const TYPE_META = {
  get bug() { return { label: '🐛 Bug', cls: 'bg-red-50 text-red-700' }; },
  get improvement() { return { label: `💡 ${i18n.t('ticketsPage.tyImprovement', 'Miglioria')}`, cls: 'bg-indigo-50 text-indigo-700' }; },
  get question() { return { label: `❓ ${i18n.t('ticketsPage.tyQuestion', 'Domanda')}`, cls: 'bg-slate-50 text-slate-700' }; },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const loc = i18n.language?.startsWith('es') ? 'es-ES' : i18n.language?.startsWith('en') ? 'en-GB' : 'it-IT';
    return d.toLocaleString(loc, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function humanSize(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function NewTicketDialog({ open, onOpenChange, onCreated }) {
  const { t: tr } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle(''); setDescription(''); setType('bug'); setPriority('medium'); setFiles([]);
  };

  const submit = async () => {
    if (title.trim().length < 3) return toast.error(tr('ticketsPage.titleTooShort', 'Titolo troppo corto'));
    if (description.trim().length < 5) return toast.error(tr('ticketsPage.descTooShort', 'Descrizione troppo corta'));
    setSubmitting(true);
    try {
      const { data: t } = await api.post('/tickets', { title: title.trim(), description: description.trim(), type, priority });
      // Upload attachments (sequential to avoid rate limits)
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        try {
          await api.post(`/tickets/${t.id}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (err) {
          toast.error(`${tr('ticketsPage.uploadError', 'Errore upload')} ${f.name}: ${err?.response?.data?.detail || tr('ticketsPage.unknown', 'sconosciuto')}`);
        }
      }
      toast.success(tr('ticketsPage.createdNotified', 'Ticket creato — Morena è stata notificata via email'));
      reset();
      onOpenChange(false);
      onCreated?.(t);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('ticketsPage.createError', 'Errore creazione ticket'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle>{tr('ticketsPage.newTicket', 'Nuovo ticket')}</DialogTitle>
          <DialogDescription>{tr('ticketsPage.newTicketDesc', 'Segnala un bug, chiedi una miglioria o poni una domanda al team.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tr('ticketsPage.titleLabel', 'Titolo')}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tr('ticketsPage.titlePlaceholder', 'Descrivi il problema in una frase…')} data-testid="ticket-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tr('ticketsPage.typeLabel', 'Tipo')}</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="ticket-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">🐛 Bug</SelectItem>
                  <SelectItem value="improvement">💡 {tr('ticketsPage.tyImprovement', 'Miglioria')}</SelectItem>
                  <SelectItem value="question">❓ {tr('ticketsPage.tyQuestion', 'Domanda')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tr('ticketsPage.priorityLabel', 'Priorità')}</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">🟢 {tr('ticketsPage.prLow', 'Bassa')}</SelectItem>
                  <SelectItem value="medium">🟡 {tr('ticketsPage.prMedium', 'Media')}</SelectItem>
                  <SelectItem value="high">🟠 {tr('ticketsPage.prHigh', 'Alta')}</SelectItem>
                  <SelectItem value="critical">🔴 {tr('ticketsPage.prCritical', 'Critica')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tr('ticketsPage.descLabel', 'Descrizione')}</label>
            <Textarea rows={6} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={tr('ticketsPage.descPlaceholder', 'Spiega passo a passo cosa succede, come riprodurlo, cosa ti aspetti…')} data-testid="ticket-description-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tr('ticketsPage.attachmentsLabel', 'Allegati (immagini, video, PDF, doc)')}</label>
            <input
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="block w-full text-sm mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[hsl(var(--primary))] file:text-[hsl(var(--primary-foreground))] hover:file:opacity-90"
              data-testid="ticket-files-input"
            />
            {files.length > 0 && (
              <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
                {files.map((f, i) => (
                  <li key={`file-${f.name}-${f.size}-${i}`} className="flex items-center gap-2">
                    <Paperclip className="w-3 h-3" /> {f.name} · {humanSize(f.size)}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-muted-foreground italic mt-1">{tr('ticketsPage.maxFileNote', 'Max 50 MB per file. Formati: JPG, PNG, GIF, MP4, MOV, PDF, DOC, XLS.')}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{tr('ticketsPage.cancel', 'Annulla')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="ticket-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            {tr('ticketsPage.createTicket', 'Crea ticket')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketDetailDialog({ ticket, open, onOpenChange, onUpdated, isAdmin }) {
  const { t: tr } = useTranslation();
  const [status, setStatus] = useState(ticket?.status || 'open');
  const [priority, setPriority] = useState(ticket?.priority || 'medium');
  const [commentText, setCommentText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ★ 2026-07-29 (hooks fix): sync local state only when a DIFFERENT ticket
  // is opened. We intentionally do NOT depend on `ticket?.status`/`.priority`
  // otherwise a parent re-render after `changeStatus` would reset the local
  // state to the STALE prop before the parent had a chance to refresh.
  useEffect(() => {
    setStatus(ticket?.status || 'open');
    setPriority(ticket?.priority || 'medium');
    setCommentText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  if (!ticket) return null;

  const changeStatus = async (v) => {
    setSaving(true);
    try {
      await api.patch(`/tickets/${ticket.id}`, { status: v });
      setStatus(v);
      toast.success(tr('ticketsPage.statusUpdated', 'Stato aggiornato'));
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('ticketsPage.updateError', 'Errore aggiornamento'));
    } finally { setSaving(false); }
  };

  const changePriority = async (v) => {
    setSaving(true);
    try {
      await api.patch(`/tickets/${ticket.id}`, { priority: v });
      setPriority(v);
      toast.success(tr('ticketsPage.priorityUpdated', 'Priorità aggiornata'));
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('ticketsPage.updateError', 'Errore aggiornamento'));
    } finally { setSaving(false); }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/tickets/${ticket.id}/comments`, { text: commentText.trim() });
      toast.success(tr('ticketsPage.commentAdded', 'Commento aggiunto'));
      setCommentText('');
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('ticketsPage.commentError', 'Errore commento'));
    } finally { setSaving(false); }
  };

  const uploadNewAtt = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        await api.post(`/tickets/${ticket.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) {
        toast.error(`${tr('ticketsPage.uploadError', 'Errore upload')} ${f.name}: ${err?.response?.data?.detail || ''}`);
      }
    }
    setUploading(false);
    toast.success(tr('ticketsPage.attachmentsUploaded', 'Allegati caricati'));
    onUpdated?.();
    e.target.value = '';
  };

  const deleteAtt = async (attId) => {
    if (!window.confirm(tr('ticketsPage.deleteAttConfirm', 'Eliminare l\u2019allegato?'))) return;
    try {
      await api.delete(`/tickets/${ticket.id}/attachments/${attId}`);
      toast.success(tr('ticketsPage.attachmentDeleted', 'Allegato eliminato'));
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('ticketsPage.genericError', 'Errore'));
    }
  };

  const attUrl = (attId) => {
    // ★ 2026-07-29 — use tokenStorage instead of raw localStorage access.
    // The query-string suffix is only used for cache-busting; auth still comes
    // from the Authorization header set by the axios interceptor (fetch here
    // is via image tag / anchor, so we rely on the same-origin cookie/token
    // pattern the browser applies for GET).
    const t = getRawToken();
    return `${process.env.REACT_APP_BACKEND_URL}/api/tickets/${ticket.id}/attachments/${attId}?_t=${t?.slice(0, 8) || ''}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
        <DialogHeader>
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <DialogTitle className="text-lg">{ticket.title}</DialogTitle>
              <DialogDescription className="text-xs mt-1 flex items-center gap-2">
                <User className="w-3 h-3" /> {ticket.author?.name} · {ticket.author?.email}
                <span>·</span>
                <CalIcon className="w-3 h-3" /> {fmtDate(ticket.created_at)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <span className={`text-xs px-2 py-1 rounded border ${TYPE_META[ticket.type]?.cls || ''}`}>{TYPE_META[ticket.type]?.label}</span>
            {isAdmin ? (
              <Select value={priority} onValueChange={changePriority} disabled={saving}>
                <SelectTrigger className="h-7 text-xs w-[120px]" data-testid="ticket-detail-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">🟢 {tr('ticketsPage.prLow', 'Bassa')}</SelectItem>
                  <SelectItem value="medium">🟡 {tr('ticketsPage.prMedium', 'Media')}</SelectItem>
                  <SelectItem value="high">🟠 {tr('ticketsPage.prHigh', 'Alta')}</SelectItem>
                  <SelectItem value="critical">🔴 {tr('ticketsPage.prCritical', 'Critica')}</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className={`text-xs px-2 py-1 rounded border ${PRIORITY_META[ticket.priority]?.cls || ''}`}>{tr('ticketsPage.priorityLabel', 'Priorità')}: {PRIORITY_META[ticket.priority]?.label}</span>
            )}
            {isAdmin ? (
              <Select value={status} onValueChange={changeStatus} disabled={saving}>
                <SelectTrigger className="h-7 text-xs w-[160px]" data-testid="ticket-detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">🆕 {tr('ticketsPage.stOpen', 'Aperto')}</SelectItem>
                  <SelectItem value="in_progress">⏳ {tr('ticketsPage.stInProgress', 'In lavorazione')}</SelectItem>
                  <SelectItem value="resolved">✅ {tr('ticketsPage.stResolved', 'Risolto')}</SelectItem>
                  <SelectItem value="closed">🔒 {tr('ticketsPage.stClosed', 'Chiuso')}</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className={`text-xs px-2 py-1 rounded border ${STATUS_META[ticket.status]?.cls || ''}`}>{STATUS_META[ticket.status]?.label}</span>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{tr('ticketsPage.descLabel', 'Descrizione')}</div>
            <div className="whitespace-pre-wrap text-sm bg-[var(--surface-2)] p-3 rounded-md border" data-testid="ticket-detail-description">{ticket.description}</div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tr('ticketsPage.attachments', 'Allegati')} ({(ticket.attachments || []).length})</div>
              <label className="cursor-pointer text-xs text-[hsl(var(--primary))] hover:underline">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : <Plus className="w-3 h-3 inline mr-1" />}{tr('ticketsPage.add', 'Aggiungi')}
                <input type="file" multiple hidden onChange={uploadNewAtt} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" />
              </label>
            </div>
            {(ticket.attachments || []).length === 0 ? (
              <div className="text-xs text-muted-foreground italic">{tr('ticketsPage.noAttachments', 'Nessun allegato')}</div>
            ) : (
              <ul className="space-y-1">
                {ticket.attachments.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-sm bg-[var(--surface-2)] rounded-md px-2 py-1.5">
                    <Paperclip className="w-3 h-3 shrink-0" />
                    <span className="flex-1 truncate">{a.filename}</span>
                    <span className="text-xs text-muted-foreground">{humanSize(a.size)}</span>
                    <a href={attUrl(a.id)} target="_blank" rel="noopener noreferrer" className="text-xs text-[hsl(var(--primary))] hover:underline flex items-center gap-1" data-testid={`ticket-att-download-${a.id}`}>
                      <Download className="w-3 h-3" /> {tr('ticketsPage.open', 'Apri')}
                    </a>
                    <button onClick={() => deleteAtt(a.id)} className="text-xs text-red-600 hover:underline" data-testid={`ticket-att-delete-${a.id}`}><Trash2 className="w-3 h-3" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{tr('ticketsPage.comments', 'Commenti')} ({(ticket.comments || []).length})</div>
            <div className="space-y-2 mb-2">
              {(ticket.comments || []).map((c) => (
                <div key={c.id} className="bg-[var(--surface-2)] p-2 rounded-md text-sm">
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mb-0.5">
                    <User className="w-3 h-3" /> {c.author_name} · {fmtDate(c.created_at)}
                  </div>
                  <div className="whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea rows={2} value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder={tr('ticketsPage.commentPlaceholder', 'Aggiungi un commento…')} data-testid="ticket-comment-input" />
              <Button size="sm" onClick={addComment} disabled={saving || !commentText.trim()} data-testid="ticket-comment-submit"><MessageSquare className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TicketsPage() {
  const { t: tr } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [aggregates, setAggregates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [newOpen, setNewOpen] = useState(false);
  const [detailTicket, setDetailTicket] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      const { data } = await api.get('/tickets', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setAggregates(data.aggregates || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('ticketsPage.loadError', 'Errore caricamento ticket'));
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  const refreshDetail = async () => {
    if (!detailTicket) return;
    try {
      const { data } = await api.get(`/tickets/${detailTicket.id}`);
      setDetailTicket(data);
      load();
    } catch (err) {
      // Refresh dopo update fallito: non blocchiamo l'UI ma logghiamo.
      logger.warn('[TicketsPage] refresh detail ticket failed:', err?.message || err);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="tickets-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">{tr('ticketsPage.pageTitle', 'Ticket · Bug & Migliorie')}</h1>
          <p className="text-sm text-muted-foreground">{tr('ticketsPage.pageSubtitle', 'Segnala problemi, chiedi migliorie, poni domande.')} {isAdmin ? tr('ticketsPage.adminScope', 'Vedi tutti i ticket del team.') : tr('ticketsPage.userScope', 'Vedi i tuoi ticket personali.')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="tickets-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" />{tr('ticketsPage.refresh', 'Aggiorna')}</Button>
          <Button onClick={() => setNewOpen(true)} data-testid="tickets-new-btn"><Plus className="w-4 h-4 mr-1" />{tr('ticketsPage.newTicket', 'Nuovo ticket')}</Button>
        </div>
      </div>

      {/* Aggregates */}
      {aggregates && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(aggregates.by_status || {}).map(([k, v]) => (
            <Card key={k} className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === k ? 'ring-2 ring-[hsl(var(--primary))]' : ''}`} onClick={() => setStatusFilter(statusFilter === k ? 'all' : k)}>
              <CardContent className="p-3">
                <div className="text-xs text-muted-foreground">{STATUS_META[k]?.label}</div>
                <div className="text-2xl font-semibold">{v}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9" data-testid="tickets-status-filter"><SelectValue placeholder={tr('ticketsPage.status', 'Stato')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tr('ticketsPage.allStatuses', 'Tutti gli stati')}</SelectItem>
              <SelectItem value="open">🆕 {tr('ticketsPage.stOpenPl', 'Aperti')}</SelectItem>
              <SelectItem value="in_progress">⏳ {tr('ticketsPage.stInProgress', 'In lavorazione')}</SelectItem>
              <SelectItem value="resolved">✅ {tr('ticketsPage.stResolvedPl', 'Risolti')}</SelectItem>
              <SelectItem value="closed">🔒 {tr('ticketsPage.stClosedPl', 'Chiusi')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[140px] h-9" data-testid="tickets-type-filter"><SelectValue placeholder={tr('ticketsPage.typeLabel', 'Tipo')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tr('ticketsPage.allTypes', 'Tutti i tipi')}</SelectItem>
              <SelectItem value="bug">🐛 Bug</SelectItem>
              <SelectItem value="improvement">💡 {tr('ticketsPage.tyImprovement', 'Miglioria')}</SelectItem>
              <SelectItem value="question">❓ {tr('ticketsPage.tyQuestion', 'Domanda')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[140px] h-9" data-testid="tickets-priority-filter"><SelectValue placeholder={tr('ticketsPage.priorityLabel', 'Priorità')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tr('ticketsPage.allPriorities', 'Tutte')}</SelectItem>
              <SelectItem value="low">{tr('ticketsPage.prLow', 'Bassa')}</SelectItem>
              <SelectItem value="medium">{tr('ticketsPage.prMedium', 'Media')}</SelectItem>
              <SelectItem value="high">{tr('ticketsPage.prHigh', 'Alta')}</SelectItem>
              <SelectItem value="critical">{tr('ticketsPage.prCritical', 'Critica')}</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground ml-auto">{tr('ticketsPage.total', 'Totale')}: <strong>{total}</strong></div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-14 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <LifeBuoyEmpty />
              <p className="text-sm mt-2">{tr('ticketsPage.noTicketsFiltered', 'Nessun ticket con questi filtri.')}</p>
              <Button size="sm" className="mt-3" onClick={() => setNewOpen(true)}><Plus className="w-4 h-4 mr-1" />{tr('ticketsPage.openFirst', 'Apri il primo ticket')}</Button>
            </div>
          ) : (
            <div className="divide-y">
              {items.map((t) => (
                <div key={t.id} className="p-3 hover:bg-[var(--surface-2)] cursor-pointer transition-colors" onClick={() => setDetailTicket(t)} data-testid={`ticket-row-${t.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${TYPE_META[t.type]?.cls || ''}`}>{TYPE_META[t.type]?.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY_META[t.priority]?.cls || ''}`}>{PRIORITY_META[t.priority]?.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_META[t.status]?.cls || ''}`}>{STATUS_META[t.status]?.label}</span>
                        {(t.attachments || []).length > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{t.attachments.length}</span>}
                        {(t.comments || []).length > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{t.comments.length}</span>}
                      </div>
                      <div className="font-medium text-sm mt-1 truncate" data-testid={`ticket-title-${t.id}`}>{t.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-1">{t.description}</div>
                      <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                        <User className="w-3 h-3" /> {t.author?.name}
                        <span>·</span>
                        <CalIcon className="w-3 h-3" /> {fmtDate(t.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <NewTicketDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
      <TicketDetailDialog
        ticket={detailTicket}
        open={!!detailTicket}
        onOpenChange={(v) => !v && setDetailTicket(null)}
        onUpdated={refreshDetail}
        isAdmin={isAdmin}
      />
    </div>
  );
}

function LifeBuoyEmpty() {
  return (
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--surface-2)]">
      <MessageSquare className="w-6 h-6 text-muted-foreground" />
    </div>
  );
}
